import React from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, FileText, Clock, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import EnquiryForm from "@/components/EnquiryForm";
import Reveal from "@/components/Reveal";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { SITE } from "@/lib/site";
import { SERVICE_SEO } from "@/lib/service-seo";
import { getServiceBySlug } from "@/lib/services-data";

export default function ServiceDetailPage() {
 const { slug } = useParams();
 const svc = getServiceBySlug(slug);
 const fallback = SERVICE_SEO[slug];

 if (!svc) {
 return (
 <div className="max-w-[900px] mx-auto px-5 py-32 text-center" data-testid="page-service-notfound">
 <Seo title="Service not found — HG Corporates" description="This service could not be found." path={`/services/${slug || ""}`} noindex />
 <h1 className="font-display text-4xl font-black">Service not found</h1>
 <Link to="/services" className="mt-6 inline-block text-[#0F1115] link-underline font-bold">
 Back to all services
 </Link>
 </div>
 );
 }

 return (
 <div data-testid="page-service-detail">
 <Seo
  title={fallback?.title || `${svc.title} | HG Corporates`}
  description={fallback?.short || svc.short}
  path={`/services/${svc.slug}`}
  jsonLd={{
    "@context": "https://schema.org",
    "@type": "Service",
    name: svc.title,
    description: svc.description,
    provider: {
      "@type": "Organization",
      name: "HG Corporates",
      url: "https://hgcorporates.com/",
    },
    areaServed: [
      { "@type": "City", name: "Mumbai" },
      { "@type": "City", name: "Navi Mumbai" },
      { "@type": "City", name: "Thane" },
    ],
    serviceType: svc.title,
  }}
  breadcrumbs={[
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: svc.title, path: `/services/${svc.slug}` },
  ]}
 />
 <PageHero eyebrow="Service" title={svc.title} subtitle={svc.short} />

 <section className="bg-white">
 <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
 <div className="lg:col-span-8">
 <Reveal>
 <p className="text-[17px] md:text-[19px] text-[#1C1F26] leading-relaxed">
 {svc.description}
 </p>
 </Reveal>

 <Reveal delay={80}>
 <div className="mt-12">
 <h2 className="font-display text-[28px] md:text-[36px] font-black text-[#0F1115]">
 What's included
 </h2>
 <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
 {svc.inclusions.map((it) => (
 <li key={it} className="flex items-start gap-3 text-[15px] text-[#0F1115]">
 <CheckCircle2 size={18} className="text-[#FDD805] mt-0.5 shrink-0" />
 <span>{it}</span>
 </li>
 ))}
 </ul>
 </div>
 </Reveal>

 <Reveal delay={140}>
 <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
 <div className="bg-white border border-[#EAEAEA] rounded-2xl p-6">
 <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-bold text-[#4A4A4A]">
 <FileText size={14} /> Required documents
 </div>
 <ul className="mt-4 space-y-2 text-[15px]">
 {svc.required_docs.map((d) => (
 <li key={d} className="flex items-start gap-2">
 <span className="w-1.5 h-1.5 rounded-full bg-[#FDD805] mt-2.5 shrink-0" />
 {d}
 </li>
 ))}
 </ul>
 </div>
 <div className="bg-[#0F1115] text-white rounded-2xl p-6">
 <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-bold text-[#FDD805]">
 <Clock size={14} /> Turnaround
 </div>
 <div className="mt-3 font-display text-[26px] font-black">
 {svc.timeline}
 </div>
 <p className="mt-2 text-[14px] text-[#c9c9c9]">
 Subject to document readiness and MCA processing.
 </p>
 <a
 href={SITE.whatsappUrl}
 target="_blank"
 rel="noreferrer"
 className="mt-5 inline-flex items-center gap-2 bg-[#25D366] text-white rounded-full px-5 py-2.5 font-bold text-[13px] hover:bg-[#1EBE57] transition-colors"
 data-testid="service-whatsapp-cta"
 >
 <WhatsAppIcon size={14} /> Ask on WhatsApp
 </a>
 </div>
 </div>
 </Reveal>

 <div className="mt-12">
 <Link
 to="/services"
 className="text-[14px] font-bold text-[#0F1115] inline-flex items-center gap-1.5 link-underline"
 >
 <ArrowRight size={14} className="rotate-180" /> All services
 </Link>
 </div>
 </div>

 <div className="lg:col-span-4">
 <div className="sticky top-24">
 <EnquiryForm compact sourcePage={`service:${svc.slug}`} />
 </div>
 </div>
 </div>
 </section>
 </div>
 );
}
